"use client";
import { useState } from "react";
import { motion } from "framer-motion";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useTranslation } from "react-i18next";
import ContactSalesModal from "@/components/ContactSalesModal";
import LoginModal from "@/components/LoginModal";
import BrandLogo from "@/components/BrandLogo";
import { useComingSoon } from "@/contexts/ComingSoonContext";

const NAV_LINKS = [
  { href: "#problem", key: "nav.why" },
  { href: "#solution", key: "nav.solution" },
  { href: "#compliance", key: "nav.compliance" },
  { href: "#differentiation", key: "nav.approach" },
  { href: "#pricing", key: "nav.pricing" },
] as const;

const Header = () => {
  const { t } = useTranslation();
  const comingSoon = useComingSoon();
  const [salesModalOpen, setSalesModalOpen] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const openSales = () => {
    setMobileOpen(false);
    setSalesModalOpen(true);
  };

  const openLogin = () => {
    setMobileOpen(false);
    if (!comingSoon) setLoginModalOpen(true);
  };

  return (
    <>
      <ContactSalesModal open={salesModalOpen} onOpenChange={setSalesModalOpen} />
      <LoginModal open={loginModalOpen} onOpenChange={setLoginModalOpen} />
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="fixed top-0 inset-x-0 z-50 bg-background/80 backdrop-blur-md border-b border-border overflow-x-clip"
      >
        <div className="container mx-auto flex h-14 sm:h-16 items-center gap-3 px-4 sm:px-6 lg:px-8 min-w-0">
          {/* Brand */}
          <div className="min-w-0 flex-1 lg:flex-none">
            <BrandLogo
              asLink
              className="max-w-full"
              imgClassName="h-7 sm:h-8 lg:h-9 w-auto max-w-[min(100%,10rem)] sm:max-w-[min(100%,13rem)] lg:max-w-none object-contain object-left"
            />
          </div>

          {/* Desktop nav — lg+ only */}
          <nav
            className="hidden lg:flex flex-1 items-center justify-center gap-5 xl:gap-7 text-sm font-medium text-muted-foreground"
            aria-label="Primary"
          >
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="hover:text-foreground transition-colors whitespace-nowrap"
              >
                {String(t(link.key))}
              </a>
            ))}
          </nav>

          {/* Desktop actions — lg+ only */}
          <div className="hidden lg:flex items-center gap-3 shrink-0">
            <LanguageSwitcher />
            <span className="relative inline-flex">
              <Button
                variant="outline"
                size="sm"
                disabled={comingSoon}
                onClick={() => !comingSoon && setLoginModalOpen(true)}
              >
                {String(t("nav.login"))}
              </Button>
              {comingSoon && (
                <span className="absolute -top-1.5 -right-1 text-[10px] font-medium text-muted-foreground px-1.5 py-0.5 rounded bg-muted border border-border shadow-sm whitespace-nowrap pointer-events-none">
                  {String(t("auth.comingSoon"))}
                </span>
              )}
            </span>
            <Button size="sm" onClick={() => setSalesModalOpen(true)}>
              {String(t("nav.requestBriefing"))}
            </Button>
          </div>

          {/* Compact bar — below lg: language + menu, no collisions */}
          <div className="flex lg:hidden items-center gap-2 shrink-0">
            <LanguageSwitcher />
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  aria-label={String(t("nav.menu"))}
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[min(100vw-1.5rem,20rem)] p-0 flex flex-col gap-0">
                <SheetHeader className="px-5 pt-5 pb-4 border-b border-border space-y-0 text-left">
                  <SheetTitle className="sr-only">{String(t("nav.menu"))}</SheetTitle>
                  <BrandLogo imgClassName="h-8" />
                </SheetHeader>

                <nav className="flex flex-col gap-0.5 px-3 py-4 flex-1 overflow-y-auto" aria-label="Primary">
                  {NAV_LINKS.map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      onClick={() => setMobileOpen(false)}
                      className="rounded-md px-3 py-2.5 text-sm font-medium text-foreground hover:bg-secondary transition-colors"
                    >
                      {String(t(link.key))}
                    </a>
                  ))}
                </nav>

                <div className="border-t border-border px-5 py-4 space-y-3 mt-auto">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
                      {String(t("nav.language"))}
                    </span>
                    <LanguageSwitcher />
                  </div>
                  <div className="relative">
                    <Button
                      variant="outline"
                      className="w-full"
                      disabled={comingSoon}
                      onClick={openLogin}
                    >
                      {String(t("nav.login"))}
                    </Button>
                    {comingSoon && (
                      <span className="absolute -top-2 right-2 text-[10px] font-medium text-muted-foreground px-1.5 py-0.5 rounded bg-muted border border-border shadow-sm">
                        {String(t("auth.comingSoon"))}
                      </span>
                    )}
                  </div>
                  <Button className="w-full" onClick={openSales}>
                    {String(t("nav.requestBriefing"))}
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </motion.header>
    </>
  );
};

export default Header;
